import { sql } from "drizzle-orm";
import { check, index, integer, real, sqliteTable, text } from "drizzle-orm/sqlite-core";

type InvoiceDocumentType = "sales" | "purchase" | "credit_note" | "debit_note";
type InvoicePartyType = "customer" | "supplier";

function quoted(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

function createInvoiceDocumentTable(
  tableName: string,
  indexPrefix: string,
  defaultInvoiceType: InvoiceDocumentType,
  defaultPartyType: InvoicePartyType,
) {
  return sqliteTable(tableName, {
    id: text("id").primaryKey(),
    invoiceNo: text("invoice_no").notNull().unique(),
    companyId: text("company_id").notNull(),
    branchId: text("branch_id"),
    invoiceType: text("invoice_type", {
      enum: ["sales", "purchase", "credit_note", "debit_note"],
    }).notNull().default(defaultInvoiceType),
    partyType: text("party_type", {
      enum: ["customer", "supplier"],
    }).notNull().default(defaultPartyType),
    partyId: text("party_id").notNull(),
    invoiceDate: text("invoice_date").notNull(),
    dueDate: text("due_date"),
    subtotal: real("subtotal").notNull().default(0),
    taxAmount: real("tax_amount").notNull().default(0),
    totalDiscount: real("total_discount").default(0),
    totalWht: real("total_wht").default(0),
    total: real("total").notNull().default(0),
    outstanding: real("outstanding").notNull().default(0),
    docStatus: integer("doc_status").notNull().default(0),
    status: text("status", {
      enum: ["draft", "submitted", "partially_paid", "paid", "overdue", "cancelled"],
    }).notNull().default("draft"),
    creditNoteOf: text("credit_note_of"),
    debitNoteOf: text("debit_note_of"),
    reason: text("reason"),
    originalInvoiceAmount: real("original_invoice_amount"),
    correctedAmount: real("corrected_amount"),
    differenceAmount: real("difference_amount"),
    reasonCode: text("reason_code", {
      enum: [
        "discount",
        "return",
        "price_error",
        "short_delivery",
        "excess_delivery",
        "quality_issue",
        "other",
      ],
    }),
    currencyCode: text("currency_code").default("THB"),
    exchangeRate: real("exchange_rate").default(1),
    transactionType: text("transaction_type"),
    deliveryDate: text("delivery_date"),
    taxPointDate: text("tax_point_date"),
    vatStatus: text("vat_status", {
      enum: ["undue", "due", "reported", "exempt"],
    }).default("due"),
    vatPeriod: text("vat_period"),
    remarks: text("remarks"),
    referenceModule: text("reference_module"),
    referenceDoctype: text("reference_doctype"),
    referenceId: text("reference_id"),
    createdBy: text("created_by"),
    createdAt: text("created_at").notNull(),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
    deletedAt: text("deleted_at"),
    deletedBy: text("deleted_by"),
  }, (table) => ({
    companyStatusIdx: index(`${indexPrefix}_company_status_idx`).on(table.companyId, table.status, table.invoiceDate),
    companyPartyIdx: index(`${indexPrefix}_company_party_idx`).on(table.companyId, table.partyType, table.partyId),
    companyTypeIdx: index(`${indexPrefix}_company_type_idx`).on(table.companyId, table.invoiceType, table.status),
    companyBranchIdx: index(`${indexPrefix}_company_branch_idx`).on(table.companyId, table.branchId, table.status),
    creditNoteOfIdx: index(`${indexPrefix}_credit_note_of_idx`).on(table.creditNoteOf),
    debitNoteOfIdx: index(`${indexPrefix}_debit_note_of_idx`).on(table.debitNoteOf),
    companyVatPeriodIdx: index(`${indexPrefix}_company_vat_period_idx`).on(table.companyId, table.vatPeriod, table.vatStatus),
    companyTaxPointIdx: index(`${indexPrefix}_company_tax_point_idx`).on(table.companyId, table.taxPointDate, table.vatStatus),
    ownerCheck: check(
      `${tableName}_owner_chk`,
      sql`${table.invoiceType} = ${sql.raw(quoted(defaultInvoiceType))}
        AND ${table.partyType} = ${sql.raw(quoted(defaultPartyType))}`,
    ),
  }));
}

export const accountingSalesInvoices = createInvoiceDocumentTable(
  "accounting_sales_invoices",
  "asi",
  "sales",
  "customer",
);

export const accountingPurchaseInvoices = createInvoiceDocumentTable(
  "accounting_purchase_invoices",
  "apinv",
  "purchase",
  "supplier",
);

export const accountingSalesCreditNotes = createInvoiceDocumentTable(
  "accounting_sales_credit_notes",
  "ascn",
  "credit_note",
  "customer",
);

export const accountingPurchaseCreditNotes = createInvoiceDocumentTable(
  "accounting_purchase_credit_notes",
  "apcn",
  "credit_note",
  "supplier",
);

export const accountingSalesDebitNotes = createInvoiceDocumentTable(
  "accounting_sales_debit_notes",
  "asdn",
  "debit_note",
  "customer",
);

export const accountingPurchaseDebitNotes = createInvoiceDocumentTable(
  "accounting_purchase_debit_notes",
  "apdn",
  "debit_note",
  "supplier",
);

export function accountingInvoiceTableForRecord(input: {
  invoiceType?: string | null;
  partyType?: string | null;
}) {
  if (input.invoiceType === "sales") return accountingSalesInvoices;
  if (input.invoiceType === "purchase") return accountingPurchaseInvoices;
  if (input.invoiceType === "credit_note" && input.partyType === "supplier") return accountingPurchaseCreditNotes;
  if (input.invoiceType === "credit_note") return accountingSalesCreditNotes;
  if (input.invoiceType === "debit_note" && input.partyType === "customer") return accountingSalesDebitNotes;
  if (input.invoiceType === "debit_note") return accountingPurchaseDebitNotes;
  return null;
}

export type AccountingSalesInvoice = typeof accountingSalesInvoices.$inferSelect;
export type NewAccountingSalesInvoice = typeof accountingSalesInvoices.$inferInsert;
export type AccountingPurchaseInvoice = typeof accountingPurchaseInvoices.$inferSelect;
export type NewAccountingPurchaseInvoice = typeof accountingPurchaseInvoices.$inferInsert;
export type AccountingSalesCreditNote = typeof accountingSalesCreditNotes.$inferSelect;
export type NewAccountingSalesCreditNote = typeof accountingSalesCreditNotes.$inferInsert;
export type AccountingPurchaseCreditNote = typeof accountingPurchaseCreditNotes.$inferSelect;
export type NewAccountingPurchaseCreditNote = typeof accountingPurchaseCreditNotes.$inferInsert;
export type AccountingSalesDebitNote = typeof accountingSalesDebitNotes.$inferSelect;
export type NewAccountingSalesDebitNote = typeof accountingSalesDebitNotes.$inferInsert;
export type AccountingPurchaseDebitNote = typeof accountingPurchaseDebitNotes.$inferSelect;
export type NewAccountingPurchaseDebitNote = typeof accountingPurchaseDebitNotes.$inferInsert;
